import { useMemo, useState } from "react"
import styled from "styled-components"

import { colors, spacing } from "@minuthq/meatball-ui-react"
import {
  addWeeks,
  endOfWeek,
  startOfDay,
  startOfWeek,
  subWeeks,
} from "date-fns"

import { TimezoneSelect } from "src/components/Reports/TimezoneSelect"
import { WeekNavigation } from "src/components/TaskManagement/WeekNavigation"
import { useFetchTaskTableData } from "src/data/taskManagement/hooks/useFetchTaskTableData"
import { TTimezone } from "src/data/timezones/timezones"
import { useGetUser } from "src/data/user/hooks/useGetUser"
import { useTranslate } from "src/i18n/useTranslate"
import { GanttChart } from "src/ui/Graphs/GanttChart"
import { TGanttChartSeriesData } from "src/ui/Graphs/graphTypes"
import { LoadingScreen } from "src/ui/LoadingScreen/LoadingScreen"
import { MBadge } from "src/ui/MBadge/MBadge"
import {
  getStoredSelectedTimezone,
  getWeekStart,
  homeTimezoneToUTC,
} from "src/utils/l10n"

export function TaskCalendarView() {
  const { clock_type } = useGetUser()
  const { t, langKeys } = useTranslate()
  const { timezone: storedTimezone } = getStoredSelectedTimezone()

  const weekStartsOn = getWeekStart()

  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const now = new Date()
    return startOfWeek(now, { weekStartsOn })
  })

  const [selectedTimezone, setSelectedTimezone] =
    useState<TTimezone>(storedTimezone)

  const { loading, tasks, homes, error } = useFetchTaskTableData({
    limit: 100,
    offset: 0,
    sort_by: "earliest_start_date",
    sort_order: "asc",
  })

  function goToPreviousWeek() {
    setCurrentWeekStart((prev) => subWeeks(prev, 1))
  }

  function goToCurrentWeek() {
    const now = new Date()
    setCurrentWeekStart(startOfWeek(now, { weekStartsOn }))
  }

  function goToNextWeek() {
    setCurrentWeekStart((prev) => addWeeks(prev, 1))
  }

  const currentWeekEnd = endOfWeek(currentWeekStart, { weekStartsOn })

  const chartData: TGanttChartSeriesData[] = useMemo(() => {
    const series = homes.map((home): TGanttChartSeriesData => {
      const data = tasks
        .filter((t) => t.home_id === home.home_id)
        .map((task) => {
          const due = new Date(task.time_window.due_date)

          const fillColor =
            task.status === "done"
              ? colors.systemGoodLight
              : colors.systemInfoLight
          const textColor =
            task.status === "done" ? colors.systemGoodDark : undefined

          return {
            name: task.title,
            start: startOfDay(due).getTime(),
            end: due.getTime(),
            fillColor,
            textColor,
          }
        })

      return { name: home.name || "", data }
    })

    return series.filter((series) => series.data.length > 0)
  }, [homes, tasks])

  const primaryXAxisOptions = useMemo(
    () => ({
      min: homeTimezoneToUTC(
        currentWeekStart,
        selectedTimezone.tzCode
      ).getTime(),
      max: homeTimezoneToUTC(currentWeekEnd, selectedTimezone.tzCode).getTime(),
    }),
    [currentWeekStart, currentWeekEnd, selectedTimezone.tzCode]
  )

  if (loading) {
    return <LoadingScreen debugInfo={false} />
  }

  if (error) {
    return (
      <MBadge color="error">
        {t(langKeys.failed_general_error_try_refreshing_page)}
      </MBadge>
    )
  }

  return (
    <div>
      <NavigationWrapper>
        <WeekNavigation
          onPreviousWeek={goToPreviousWeek}
          onCurrentWeek={goToCurrentWeek}
          onNextWeek={goToNextWeek}
        />
      </NavigationWrapper>
      <GanttChart
        seriesData={chartData}
        clockType={clock_type}
        timezone={selectedTimezone.tzCode}
        secondaryXAxisOptions={{ enabled: false }}
        showCurrentDateIndicator
        primaryXAxisOptions={primaryXAxisOptions}
      />
      <TimezoneWrapper>
        <TimezoneSelect
          timezone={selectedTimezone}
          onChange={setSelectedTimezone}
        />
      </TimezoneWrapper>
    </div>
  )
}

const NavigationWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: ${spacing.M};
`

const TimezoneWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-top: ${spacing.M};
`
